<template>
  <div class="image-editor">
    <!-- 编辑图片弹窗 -->
    <el-dialog
      :title="$t('ImageEditor.editImage')"
      :visible.sync="visible"
      :before-close="handleClose"
      width="600px"
    >
      <div class="image-container">
        <div class="upload-container">
          <el-upload
            class="avatar-uploader"
            action=""
            :show-file-list="false"
            :http-request="handleUpload"
            :before-upload="beforeUpload"
          >
            <div v-if="tempImageUrl || imageUrl" class="image-preview-wrapper">
              <img
                :src="tempImageUrl || imageUrl"
                class="preview-image"
                style="width: 453px; height: 611px;"
              >
              <el-button
                type="text"
                class="change-image-btn"
                @click="handleChangeImage"
              >
                {{ $t('ImageEditor.changeImage') }}
              </el-button>
            </div>
            <i v-else class="el-icon-plus avatar-uploader-icon"></i>
          </el-upload>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
        <el-button type="primary" @click="handleConfirm">{{ $t('common.btn.confirm') }}</el-button>
      </div>
    </el-dialog>

    <!-- 图片裁剪弹窗 -->
    <el-dialog
      :title="$t('ImageEditor.cropImage')"
      :visible.sync="cropDialogVisible"
      :before-close="handleCropDialogClose"
      width="600px"
    >
      <div class="crop-container">
        <div class="crop-box">
          <img
            ref="cropperImage"
            :src="cropImageUrl"
            style="max-width: 100%"
            @load="onImageLoaded"
          >

        </div>
      </div>

      <div class="crop-controls">
        <el-button
          size="small"
          @click="replaceImage"
        >
          {{ $t('ImageEditor.changeImage') }}
        </el-button>
        <el-button
          size="small"
          @click="rotateLeft"
        >
          <i class="el-icon-refresh-left"></i> {{ $t('ImageEditor.rotateLeft') }}
        </el-button>
        <el-button
          size="small"
          @click="rotateRight"
        >
          <i class="el-icon-refresh-right"></i> {{ $t('ImageEditor.rotateRight') }}
        </el-button>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="cropDialogVisible = false">{{ $t('common.btn.cancel') }}</el-button>
        <el-button type="primary" @click="confirmCrop">{{ $t('ImageEditor.confirmCrop') }}</el-button>
      </div>
    </el-dialog>

    <!-- 用于触发更换图片的隐藏上传组件 -->
    <el-upload
      class="hidden-upload"
      action=""
      :show-file-list="false"
      :http-request="handleUpload"
      :before-upload="beforeUpload"
      ref="replaceUpload"
    >
      <el-button ref="replaceUploadBtn" style="display: none;"></el-button>
    </el-upload>
  </div>
</template>

<script>
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';
export default {
  name: 'ImageEditor',
  props: {
    // 控制弹窗显示/隐藏
    visible: {
      type: Boolean,
      default: false
    },
    // 初始图片URL
    imageUrl: {
      type: [String, null],
      default: ''
    }
  },
  data() {
    return {
      // 临时图片URL（裁剪后的新图片）
      tempImageUrl: '',
      // 裁剪弹窗是否显示
      cropDialogVisible: false,
      // 裁剪图片URL
      cropImageUrl: '',
      // cropper实例
      cropper: null,
      // 旋转角度
      rotateDegree: 0,
      // 要上传的文件
      fileToUpload: null,
      isImageLoaded: false,
      // isProcessing: false 
    };
  },
  methods: {
    // 处理更换图片
    handleChangeImage() {
      // 清空当前图片，准备上传新图片
      this.tempImageUrl = '';
      // 触发上传
      this.$refs.replaceUploadBtn.$el.click();
    },

    // 关闭弹窗
    handleClose() {
      this.resetState();
      this.$emit('close');
    },

    // 取消操作
    handleCancel() {
      this.resetState();
      this.$emit('cancel');
    },

    // 确认操作
    handleConfirm() {
      if (this.fileToUpload) {
        // 有新图片，触发上传事件
        this.$emit('confirm', this.fileToUpload, this.tempImageUrl);
      } else {
        // 没有新图片，直接关闭
        this.resetState();
        this.$emit('no-change');
      }
    },

    // 重置状态
    resetState() {
       if (this.tempImageUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(this.tempImageUrl);
      }
      this.tempImageUrl = '';
      this.fileToUpload = null;
      this.cropDialogVisible = false;
      this.destroyCropper();
    },

    // 上传前检查
    beforeUpload(file) {
      const isImage = file.type.indexOf('image/') === 0;
      if (!isImage) {
        this.$message.error(this.$t('ImageEditor.uploadImageFile'));
        return false;
      }
      
      // 将文件转为URL并打开裁剪弹窗
      this.fileToUpload = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.cropImageUrl = e.target.result;
        this.cropDialogVisible = true;
        // 延迟初始化cropper，确保图片已加载
        this.$nextTick(() => {
          this.initCropper();
        });
      };
      reader.readAsDataURL(file);
      
      // 阻止默认上传行为
      return false;
    },

    // 处理上传（实际上是在裁剪后处理）
    handleUpload() {
      // 这里只是占位，实际上传在裁剪后处理
    },

    onImageLoaded() {
      this.isImageLoaded = true;
      this.initCropper();
    },

    // 初始化裁剪器
    initCropper() {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
      
      const image = this.$refs.cropperImage;
       if (!image || !this.isImageLoaded) {
        this.$message.warning(this.$t('ImageEditor.imageLoading'));
        return;
      }

      try {
        this.cropper = new Cropper(image, {
          aspectRatio: 0.742,
          viewMode: 1,
          dragMode: 'move',
          autoCropArea: 0.826,
          restore: false,
          guides: true,
          center: true,
          highlight: false,
          cropBoxMovable: true,
          cropBoxResizable: true,
          toggleDragModeOnDblclick: false
        });
      } catch (error) {
        console.error('初始化cropper失败:', error);
        this.$message.error(this.$t('ImageEditor.imageProcessingFailed'));
      }
    },

    // 旋转图片（左）
    rotateLeft() {
      this.rotateDegree -= 90;
      this.cropper.rotate(-90);
    },

    // 旋转图片（右）
    rotateRight() {
      this.rotateDegree += 90;
      this.cropper.rotate(90);
    },

    // 更换图片
    replaceImage() {
      this.cropDialogVisible = false;
      this.destroyCropper();  // 使用统一的销毁方法
      this.$refs.replaceUploadBtn.$el.click();
    },

    // 确认裁剪
    confirmCrop() {
      if (!this.cropper || typeof this.cropper.getCroppedCanvas !== 'function') {
        this.$message.error(this.$t('ImageEditor.cropToolNotReady'));
        return;
      }
      
      try {
        // 获取裁剪后的图片数据
        const canvas = this.cropper.getCroppedCanvas();
        canvas.toBlob(blob => {
          const newImageUrl = URL.createObjectURL(blob);
          this.tempImageUrl = newImageUrl;
          this.fileToUpload = new File([blob], this.fileToUpload.name, {
            type: this.fileToUpload.type,
            lastModified: Date.now()
          });
        });
        
        this.cropDialogVisible = false;
        this.destroyCropper();
      } catch (error) {
        console.error('裁剪图片失败:', error);
        this.$message.error(this.$t('ImageEditor.cropFailed'));
        this.destroyCropper();
      }
    },

    destroyCropper() {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
      this.isImageLoaded = false;
      this.rotateDegree = 0;
    },

    // 关闭裁剪弹窗
    handleCropDialogClose() {
      this.cropDialogVisible = false;
      this.destroyCropper(); 
    }
  },
  beforeDestroy() {
    this.resetState();
  }
};
</script>

<style scoped>
/* .image-editor {
  display: none;
} */

.image-container {
  text-align: center;
  padding: 20px 0;
}

.avatar-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
  display: inline-block;
  width: 100%;
  height: 611px;
}

.avatar-uploader:hover {
  border-color: #409eff;
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 453px;
  height: 611px;
  line-height: 611px;
  text-align: center;
  display: inline-block;
}

.image-preview-wrapper {
  position: relative;
  display: inline-block;
}

.preview-image {
  border-radius: 4px;
}

.change-image-btn {
  position: absolute;
  bottom: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white !important;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.change-image-btn:hover {
  background-color: rgba(0, 0, 0, 0.7);
  color: white !important;
}

.crop-container {
  padding: 10px 0;
}

.crop-box {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 10px;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.crop-controls {
  margin-top: 20px;
  display: flex;
  gap: 10px;
  justify-content: center;
}

.hidden-upload {
  display: none;
}
</style>
